import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Container, CircularProgress, Alert, Fade } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRightArrowLeft } from '@fortawesome/free-solid-svg-icons';
import useMovementAnalytics from '../hooks/useMovementAnalytics';
import MovementFilter from '../components/movement-analytics/MovementFilter';
import MovementSummaryCards from '../components/movement-analytics/MovementSummaryCards';
import MovementProductsTable from '../components/movement-analytics/MovementProductsTable';
import MovementDrilldownModal from '../components/movement-analytics/MovementDrilldownModal';

const getDefaultDateRange = () => {
    const today = new Date();
    const from = new Date();
    from.setDate(today.getDate() - 7);
    return { from: from.toISOString().split('T')[0], to: today.toISOString().split('T')[0] };
};

const TABS = [
    { id: 'summary',  label: 'Summary' },
    { id: 'products', label: 'Products' },
];

const MovementAnalyticsPage = () => {
    const {
        summary, products, productsTotal,
        sizeSplit, sizeSplitLoading, sizeSplitError,
        productOrders, productOrdersLoading, productOrdersError,
        fetching, error,
        fetchSummary, fetchProducts, fetchSizeSplit, fetchProductOrders,
        exporting, exportSizeSplit,
        clearDrilldown, clearProductOrders,
    } = useMovementAnalytics();

    const [dateRange, setDateRange] = useState(getDefaultDateRange);
    const [activeTab, setActiveTab] = useState('summary');
    const [sortBy, setSortBy] = useState('total_movement');

    const [modalOpen, setModalOpen] = useState(false);
    const [drillLevel, setDrillLevel] = useState('sizes'); // 'sizes' | 'orders'
    const [activeCatalogueId, setActiveCatalogueId] = useState(null);
    const [activeSubCategoryName, setActiveSubCategoryName] = useState('');
    const [activeProductLabel, setActiveProductLabel] = useState('');
    const [exportError, setExportError] = useState('');

    const isTabEffectReady = useRef(false);

    const filterRef = useRef({});
    filterRef.current = { dateRange, sortBy };

    const buildBaseParams = useCallback(() => {
        const { dateRange: dr } = filterRef.current;
        return { from_date: dr.from, to_date: dr.to };
    }, []);

    const fetchForTab = useCallback(async (tab) => {
        const base = buildBaseParams();
        if (tab === 'summary') {
            await fetchSummary(base);
        } else if (tab === 'products') {
            await fetchProducts({ ...base, sort_by: filterRef.current.sortBy, limit: 100 });
        }
    }, [buildBaseParams, fetchSummary, fetchProducts]);

    // Initial load
    useEffect(() => {
        fetchForTab('summary').finally(() => { isTabEffectReady.current = true; });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Tab change
    useEffect(() => {
        if (!isTabEffectReady.current) return;
        fetchForTab(activeTab);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const handleApply = () => fetchForTab(activeTab);

    // Sub Category row (2b) → open modal at level 1 (size split, 2d)
    const handleRowClick = (row) => {
        setActiveCatalogueId(row.product_id);
        setActiveSubCategoryName(row.product_name);
        setActiveProductLabel('');
        setExportError('');
        setDrillLevel('sizes');
        setModalOpen(true);
        fetchSizeSplit(row.product_id, buildBaseParams());
    };

    // Export the size split as CSV — reuses the main-screen date range + sort.
    const handleExport = async () => {
        if (!activeCatalogueId) return;
        setExportError('');
        const params = { ...buildBaseParams(), sort_by: filterRef.current.sortBy };
        const res = await exportSizeSplit(activeCatalogueId, params);
        if (!res || res.success === false) {
            setExportError(res?.error || 'Failed to export CSV. Please try again.');
            return;
        }
        // Derive filename from Content-Disposition, else build a sensible default.
        const cd = res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'] || '';
        const match = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)/i);
        const base = buildBaseParams();
        const fallback = `movement_${(activeSubCategoryName || 'sub_category').replace(/\s+/g, '_')}_${base.from_date}_${base.to_date}.csv`;
        const filename = match ? decodeURIComponent(match[1]) : fallback;

        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    // Product (size) row (2d) → drill to level 2 (orders, 2e)
    const handleProductClick = (p) => {
        setActiveProductLabel(p.alias_name || `#${p.product_id}`);
        setDrillLevel('orders');
        fetchProductOrders(p.product_id, buildBaseParams());
    };

    // Breadcrumb back: orders → size split
    const handleBack = () => {
        setDrillLevel('sizes');
        clearProductOrders();
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setDrillLevel('sizes');
        clearDrilldown();
    };

    return (
        <Container maxWidth={false} sx={{ py: 4 }}>
            {/* Header */}
            <Box mb={5}>
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                    <Box sx={{
                        bgcolor: '#189AB4', borderRadius: 2, width: 40, height: 40,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white',
                    }}>
                        <FontAwesomeIcon icon={faArrowRightArrowLeft} />
                    </Box>
                    <Typography variant="h4" fontWeight="800" letterSpacing="-0.5px">
                        Movement Analysis
                    </Typography>
                </Box>
                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 780 }}>
                    Stock-item movement by weight — purchases (inbound) vs sales (outbound), product-wise.
                    Counts challan-generated records only. Warehouse is selected from the sidebar.
                </Typography>
            </Box>

            {/* Filter */}
            <MovementFilter
                fromDate={dateRange.from}
                toDate={dateRange.to}
                onFromDateChange={(v) => setDateRange((p) => ({ ...p, from: v }))}
                onToDateChange={(v) => setDateRange((p) => ({ ...p, to: v }))}
                sortBy={sortBy}
                onSortByChange={setSortBy}
                showSortBy={activeTab === 'products'}
                onSubmit={handleApply}
                loading={fetching}
            />

            {/* Tabs */}
            <Box sx={{ display: 'flex', gap: 1, mb: 3, borderBottom: '2px solid #f3f4f6', flexWrap: 'wrap' }}>
                {TABS.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        style={{
                            padding: '10px 20px', border: 'none',
                            borderBottom: activeTab === tab.id ? '3px solid #189AB4' : '3px solid transparent',
                            backgroundColor: 'transparent', cursor: 'pointer', fontSize: 14,
                            fontWeight: activeTab === tab.id ? 700 : 500,
                            color: activeTab === tab.id ? '#189AB4' : '#6b7280',
                            transition: 'all 0.15s', marginBottom: -2,
                        }}
                    >
                        {tab.label}
                    </button>
                ))}
            </Box>

            {/* Error */}
            {error && (
                <Fade in>
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {typeof error === 'object' ? JSON.stringify(error) : error}
                    </Alert>
                </Fade>
            )}

            {/* Initial spinner */}
            {fetching && activeTab === 'summary' && !summary && (
                <Box display="flex" justifyContent="center" p={10}>
                    <CircularProgress size={40} thickness={4} sx={{ color: '#189AB4' }} />
                </Box>
            )}

            {/* Summary tab */}
            {activeTab === 'summary' && (
                <Fade in={!!summary}>
                    <div><MovementSummaryCards summary={summary} /></div>
                </Fade>
            )}

            {/* Products tab */}
            {activeTab === 'products' && (
                <MovementProductsTable
                    items={products}
                    total={productsTotal}
                    loading={fetching}
                    onRowClick={handleRowClick}
                />
            )}

            {/* Drill-down modal: Sub Category → Product split → Orders */}
            <MovementDrilldownModal
                open={modalOpen}
                onClose={handleCloseModal}
                subCategoryName={activeSubCategoryName}
                level={drillLevel}
                sizeSplit={sizeSplit}
                sizeSplitLoading={sizeSplitLoading}
                sizeSplitError={sizeSplitError}
                productOrders={productOrders}
                productOrdersLoading={productOrdersLoading}
                productOrdersError={productOrdersError}
                activeProductLabel={activeProductLabel}
                onProductClick={handleProductClick}
                onBack={handleBack}
                onExport={handleExport}
                exporting={exporting}
                exportError={exportError}
            />
        </Container>
    );
};

export default MovementAnalyticsPage;
